// Connector properties section. Used by PropertiesPanel (right sidebar)
// when a connector is selected — same fields the old floating popover
// exposed, now living in the same surface block/group properties use.
//
// Pure presentation: receives a connector + callbacks. No context reads.

import { useEffect, useState } from 'react';
import { ArrowLeftRight, ArrowRight, GitBranch, RotateCcw, Trash2, Waves } from 'lucide-react';
import type { Connector } from '@/types/workflow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const COLOR_PRESETS = [
  'hsl(var(--primary))',
  '#0088ff',
  '#16a34a',
  '#ea580c',
  '#e11d48',
  '#7c3aed',
  '#475569',
];

export interface ConnectorPropertiesSectionProps {
  connector: Connector;
  onChange: (patch: Partial<Connector>) => void;
  onDelete: () => void;
  /** Rensa connector.waypoints (= "Återställ dragning"). */
  onResetRouting?: () => void;
  /** Create a paired return-direction connector (B→A) using opposite ports. */
  onCreateReturn?: () => void;
  /** True when no further return-flow connector can be added. */
  hasReturn?: boolean;
}

export function ConnectorPropertiesSection({
  connector, onChange, onDelete, onResetRouting, onCreateReturn, hasReturn,
}: ConnectorPropertiesSectionProps) {
  const [labelDraft, setLabelDraft] = useState(connector.label || '');
  useEffect(() => { setLabelDraft(connector.label || ''); }, [connector.id, connector.label]);

  const customColor = connector.color && !COLOR_PRESETS.includes(connector.color)
    ? connector.color : '#3b82f6';

  return (
    <div className="space-y-4" data-testid="connector-properties">
      {/* Label */}
      <div className="space-y-2">
        <Label htmlFor="connector-label">Etikett</Label>
        <Input
          id="connector-label"
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={() => onChange({ label: labelDraft.trim() })}
          onKeyDown={(e) => { if (e.key === 'Enter') onChange({ label: labelDraft.trim() }); }}
          placeholder="Skriv etikett…"
        />
      </div>

      {/* Pulse */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => onChange({ pulse: !connector.pulse })}
          className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded-md border border-border"
        >
          <Waves className="w-4 h-4" />
          <span className="flex-1">Pulsera (animerat dataflöde)</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${connector.pulse ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            {connector.pulse ? 'På' : 'Av'}
          </span>
        </button>
        {connector.pulse && (
          <div className="flex items-center gap-1.5 pl-2">
            {COLOR_PRESETS.map((c) => {
              const active = connector.pulseColor === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={`Pulsfärg ${c}`}
                  onClick={() => onChange({ pulseColor: c })}
                  className={`w-4 h-4 rounded-full border ${active ? 'ring-2 ring-ring border-ring' : 'border-border'}`}
                  style={{ background: c }}
                />
              );
            })}
            <button
              type="button"
              onClick={() => onChange({ pulseColor: undefined })}
              className="text-[11px] text-muted-foreground hover:text-foreground ml-1"
            >
              Linjefärg
            </button>
          </div>
        )}
      </div>

      {/* Color */}
      <div className="space-y-2">
        <Label>Färg</Label>
        <div className="flex items-center gap-1.5 flex-wrap">
          {COLOR_PRESETS.map((c) => {
            const active = (connector.color || COLOR_PRESETS[0]) === c;
            return (
              <button
                key={c}
                type="button"
                aria-label={`Färg ${c}`}
                onClick={() => onChange({ color: c })}
                className={`w-5 h-5 rounded-full border ${active ? 'ring-2 ring-ring border-ring' : 'border-border'}`}
                style={{ background: c }}
              />
            );
          })}
          <input
            type="color"
            value={customColor.startsWith('#') ? customColor : '#3b82f6'}
            onChange={(e) => onChange({ color: e.target.value })}
            aria-label="Egen färg"
            className="w-5 h-5 rounded-full border border-border cursor-pointer"
          />
        </div>
        <button
          type="button"
          onClick={() => onChange({ color: undefined })}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Återställ till tema
        </button>
      </div>

      {/* Bidirectional */}
      <button
        type="button"
        onClick={() => onChange({ bidirectional: !connector.bidirectional })}
        className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded-md border border-border"
      >
        {connector.bidirectional ? <ArrowLeftRight className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        <span className="flex-1">Dubbelriktad</span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${connector.bidirectional ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
          {connector.bidirectional ? 'På' : 'Av'}
        </span>
      </button>

      {/* Return flow */}
      {onCreateReturn && connector.fromId !== connector.toId && (
        <button
          type="button"
          onClick={onCreateReturn}
          disabled={hasReturn}
          className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded-md border border-border disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title={hasReturn ? 'Inga lediga portar för returflöde' : 'Skapa parallell linje i motsatt riktning'}
        >
          <GitBranch className="w-4 h-4" />
          <span className="flex-1">Skapa returflöde</span>
          {hasReturn && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Inga portar</span>
          )}
        </button>
      )}

      {/* Reset routing */}
      {onResetRouting && connector.waypoints && connector.waypoints.length > 0 && (
        <button
          type="button"
          onClick={onResetRouting}
          className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded-md border border-border"
          title="Ta bort alla manuellt placerade waypoints"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="flex-1">Återställ dragning</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {connector.waypoints.length}
          </span>
        </button>
      )}

      {/* Delete */}
      <Button
        variant="destructive"
        onClick={onDelete}
        className="w-full"
        data-testid="delete-connector"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Ta bort anslutning
      </Button>
    </div>
  );
}
